import * as React from "react";
import ChatMessageList from "./ChatMessageList";
import ChatMessageForm from "./ChatMessageForm";
import CloseChatForm from "./CloseChatForm";
import ChatHeader from "./ChatHeader";
import {
  Message,
  QuickReplyButton,
  DataField,
  SecureForm,
  Form
} from "../types";
import ChatNessageSelect from "./ChatMessageSelect";
import useChat from "../hooks/useChat";
import MessengerFlexContainer from "./MessengerFlexContainer";
import ChatForm from "./ChatForm";
import CobrowseInfoBar from "./CobrowseInfoBar";

interface Props {
  agentRating: number;
  agentName: string;
  agentHeadshotUrl: string;
  messages: Array<Message>;
  agentsTyping: Array<string>;
  showUploadProgress: boolean;
  uploadPercentage: number;
  visitorId: string;
  engagementId: string;
  engagementApiEndpoint: string;
  maxFileSizeBytes: number;
  fileSizeErrorMessage: string;
  siteId: number;
  isPopOut: boolean;
  quickReplies: QuickReplyButton[];
  dataField: DataField;
  form: Form;
  messageFormDisabled: boolean;
  secureForm: SecureForm;
  cobrowseState: string;
  onPopout: () => void;
  onCollapse: () => void;
  onShowCloseChatModal: () => void;
  onSendMessage: (text: string) => void;
  onClickQuickReply: (button: QuickReplyButton) => void;
  onSubmitDataField: (value: string) => void;
  onSubmitForm: (data: any) => void;
  onSetAgentRating: (rating: number) => void;
  onTyping: (message: string) => void;
  onClickEmailButton: () => void;
  onClickPrintButton: () => void;
  onUploadBegin: () => void;
  onUploadProgress: (percent: number) => void;
  onUploadComplete: () => void;
  onClickExit: () => void;
  onSetLineRating: (lineId: number, rating: number) => void;
  onResendMessage(message: Message): void;
  onClickActionCard: (name: string, data: string) => void;
}

/**
 * Any changes to this component or its children should be accompanied by
 * a `yarn build` and testing the Engagement Design screen in agent console.
 * Keep this component as dumb as possible so it can be easily reused.
 */

function Chat(props: Props) {
  const { chatStage, closedByAgent } = useChat();

  function chatMessageFormEnabled() {
    if (props.messageFormDisabled) {
      return false;
    }

    if (closedByAgent) {
      return false;
    }

    return true;
  }

  function renderChatMessageSelect() {
    const datafield = props.dataField;
    if (
      !datafield ||
      datafield.format !== "Dropdown" ||
      !datafield.options.length
    ) {
      return null;
    }

    return (
      <ChatNessageSelect
        options={datafield.options}
        onSubmit={props.onSubmitDataField}
      />
    );
  }

  function renderForm() {
    const form = props.form;
    if (!form) {
      return null;
    }

    return <ChatForm form={form} onSubmit={props.onSubmitForm} />;
  }

  function renderCobrowseBar() {
    if (props.cobrowseState !== "Recording") {
      return null;
    }

    return <CobrowseInfoBar />;
  }

  return (
    <MessengerFlexContainer>
      <ChatHeader
        agentRating={props.agentRating}
        onSetAgentRating={props.onSetAgentRating}
        agentName={props.agentName}
        agentHeadshotUrl={props.agentHeadshotUrl}
        onPopout={props.onPopout}
        onCollapse={props.onCollapse}
        onClose={props.onShowCloseChatModal}
        onClickEmailButton={props.onClickEmailButton}
        onClickPrintButton={props.onClickPrintButton}
        isPopout={props.isPopOut}
      />
      <ChatMessageList
        chatStage={chatStage}
        messages={props.messages}
        agentsTyping={props.agentsTyping}
        showUploadProgress={props.showUploadProgress}
        uploadPercentage={props.uploadPercentage}
        onClickExit={props.onClickExit}
        onSetLineRating={props.onSetLineRating}
        onResendMessage={props.onResendMessage}
        dataField={props.dataField}
        quickReplies={props.quickReplies}
        onSubmitDataField={props.onSubmitDataField}
        onClickQuickReply={props.onClickQuickReply}
        onClickActionCard={props.onClickActionCard}
        secureForm={props.secureForm}
      />
      {renderForm()}
      {renderChatMessageSelect()}
      {renderCobrowseBar()}
      {chatMessageFormEnabled() && (
        <ChatMessageForm
          onSendMessage={props.onSendMessage}
          onTyping={props.onTyping}
          onUploadBegin={props.onUploadBegin}
          onUploadProgress={props.onUploadProgress}
          onUploadComplete={props.onUploadComplete}
          visitorId={props.visitorId}
          engagementId={props.engagementId}
          engagementApiEndpoint={props.engagementApiEndpoint}
          maxFileSizeBytes={props.maxFileSizeBytes}
          fileSizeErrorMessage={props.fileSizeErrorMessage}
          siteId={props.siteId}
          onSubmitDataField={props.onSubmitDataField}
          dataField={props.dataField}
        />
      )}
      {closedByAgent && <CloseChatForm onClose={props.onShowCloseChatModal} />}
    </MessengerFlexContainer>
  );
}

export default Chat;
